import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface TriboPendente {
  id_tribo: number;
  slug: string;
  nome: string;
  descricao: string | null;
  tags: string[];
  oficial: boolean;
  capa_url: string | null;
  icone_url: string | null;
  data_envio: string | null;
  data_criacao: string;
  estado: 'pendente' | 'rascunho';
  criador_id: number;
  criador_nome: string;
  criador_sobrenome: string | null;
  criador_slug: string | null;
  regras_count: number;
  sementes_count: number;
}

interface TriboDetalhe {
  tribo: TriboPendente & {
    entidade_alvo_tipo: string | null;
    entidade_alvo_id: number | null;
    entidade_externa_nome: string | null;
    entidade_externa_pais: string | null;
    entidade_externa_ano: number | null;
    criador_email: string | null;
  };
  regras: { ordem: number; texto: string }[];
  sementes: {
    id_post: number;
    titulo: string;
    conteudo: string;
    data_criacao: string;
  }[];
  generos: { id_genero: number; nome_genero: string }[];
}

const TribosPendentes: React.FC = () => {
  const [items, setItems] = useState<TriboPendente[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<TriboDetalhe | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchPendentes();
  }, []);

  const fetchPendentes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/tribos/pendentes');
      setItems(response.data.items ?? []);
    } catch (err) {
      console.error('Erro ao buscar tribos pendentes:', err);
      setError('Erro ao carregar tribos pendentes.');
    } finally {
      setLoading(false);
    }
  };

  const openDetalhe = async (id_tribo: number) => {
    setActionLoading(true);
    try {
      const response = await api.get(`/admin/tribos/${id_tribo}`);
      setSelected(response.data);
    } catch (err) {
      console.error('Erro ao abrir tribo:', err);
      alert('Erro ao abrir detalhes.');
    } finally {
      setActionLoading(false);
    }
  };

  const aprovar = async (id_tribo: number) => {
    if (!window.confirm('Aprovar esta tribo? Ela vai pro ar imediatamente.')) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/tribos/${id_tribo}/aprovar`);
      alert('Tribo aprovada!');
      setSelected(null);
      fetchPendentes();
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Erro ao aprovar.');
    } finally {
      setActionLoading(false);
    }
  };

  const pedirAjuste = async (id_tribo: number) => {
    const motivo = window.prompt(
      'Motivo do pedido de ajuste (mínimo 10 caracteres, será enviado ao criador):',
    );
    if (!motivo || motivo.trim().length < 10) {
      if (motivo !== null) alert('Motivo precisa ter ao menos 10 caracteres.');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/admin/tribos/${id_tribo}/pedir-ajuste`, { motivo: motivo.trim() });
      alert('Pedido de ajuste enviado.');
      setSelected(null);
      fetchPendentes();
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Erro.');
    } finally {
      setActionLoading(false);
    }
  };

  const rejeitar = async (id_tribo: number) => {
    const motivo = window.prompt(
      'Motivo da rejeição (mínimo 10 caracteres, será enviado ao criador):',
    );
    if (!motivo || motivo.trim().length < 10) {
      if (motivo !== null) alert('Motivo precisa ter ao menos 10 caracteres.');
      return;
    }
    if (!window.confirm('Confirma a rejeição? Esta ação é definitiva.')) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/tribos/${id_tribo}/rejeitar`, { motivo: motivo.trim() });
      alert('Tribo rejeitada.');
      setSelected(null);
      fetchPendentes();
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Erro.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.headerRow}>
        <h1 style={styles.title}>Tribos pendentes</h1>
        <button onClick={fetchPendentes} disabled={loading} style={styles.refreshButton}>
          {loading ? 'Carregando…' : 'Atualizar'}
        </button>
      </header>

      {error && <p style={{ ...styles.message, color: '#dc3545' }}>{error}</p>}

      {!loading && items.length === 0 && (
        <p style={styles.message}>Nenhuma tribo aguardando aprovação.</p>
      )}

      <div style={styles.list}>
        {items.map((t) => (
          <div key={t.id_tribo} style={styles.card}>
            <div style={styles.cardIconWrap}>
              {t.icone_url ? (
                <img src={t.icone_url} alt="" style={styles.cardIcon} />
              ) : (
                <div style={styles.cardIconPlaceholder}>{t.nome[0]?.toUpperCase()}</div>
              )}
            </div>
            <div style={styles.cardBody}>
              <div style={styles.cardHeader}>
                <span style={styles.estadoBadge(t.estado)}>{t.estado.toUpperCase()}</span>
                {(t.tags ?? []).map((tag) => (
                  <span key={tag} style={styles.tipoBadge}>#{tag}</span>
                ))}
              </div>
              <h3 style={styles.cardTitle}>{t.nome}</h3>
              {t.descricao && <p style={styles.cardDesc}>{t.descricao}</p>}
              <p style={styles.cardMeta}>
                Criador: <strong>@{t.criador_slug ?? t.criador_nome}</strong> · {t.regras_count} regras ·{' '}
                {t.sementes_count} threads · enviada{' '}
                {t.data_envio
                  ? new Date(t.data_envio).toLocaleString('pt-BR')
                  : new Date(t.data_criacao).toLocaleString('pt-BR')}
              </p>
            </div>
            <div style={styles.cardActions}>
              <button
                onClick={() => openDetalhe(t.id_tribo)}
                disabled={actionLoading}
                style={{ ...styles.actionButton, backgroundColor: '#6c757d' }}
              >
                Ver
              </button>
              <button
                onClick={() => aprovar(t.id_tribo)}
                disabled={actionLoading}
                style={{ ...styles.actionButton, backgroundColor: '#28a745' }}
              >
                Aprovar
              </button>
              <button
                onClick={() => pedirAjuste(t.id_tribo)}
                disabled={actionLoading}
                style={{ ...styles.actionButton, backgroundColor: '#ffc107', color: '#000' }}
              >
                Ajuste
              </button>
              <button
                onClick={() => rejeitar(t.id_tribo)}
                disabled={actionLoading}
                style={{ ...styles.actionButton, backgroundColor: '#dc3545' }}
              >
                Rejeitar
              </button>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div style={styles.modalOverlay} onClick={(e) => {
          if (e.target === e.currentTarget) setSelected(null);
        }}>
          <div style={styles.modal}>
            <header style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{selected.tribo.nome}</h2>
              <button onClick={() => setSelected(null)} style={styles.modalClose}>
                ×
              </button>
            </header>
            <div style={styles.modalBody}>
              <p style={styles.modalMeta}>
                <strong>Slug:</strong> /tribos/{selected.tribo.slug}<br />
                <strong>Tags:</strong>{' '}
                {(selected.tribo.tags ?? []).length > 0
                  ? (selected.tribo.tags ?? []).map((tg) => `#${tg}`).join(', ')
                  : '—'}<br />
                <strong>Criador:</strong> {selected.tribo.criador_nome}{' '}
                {selected.tribo.criador_sobrenome ?? ''} ({selected.tribo.criador_email})<br />
                {selected.tribo.entidade_externa_nome && (
                  <>
                    <strong>Banda externa:</strong> {selected.tribo.entidade_externa_nome}{' '}
                    ({selected.tribo.entidade_externa_pais ?? '—'},{' '}
                    {selected.tribo.entidade_externa_ano ?? '—'})<br />
                  </>
                )}
                {selected.tribo.entidade_alvo_tipo && (
                  <>
                    <strong>Entidade alvo:</strong> {selected.tribo.entidade_alvo_tipo} #
                    {selected.tribo.entidade_alvo_id}
                    <br />
                  </>
                )}
                {selected.generos.length > 0 && (
                  <>
                    <strong>Gêneros:</strong>{' '}
                    {selected.generos.map((g) => g.nome_genero).join(', ')}
                    <br />
                  </>
                )}
              </p>

              {selected.tribo.descricao && (
                <section style={styles.section}>
                  <h3 style={styles.sectionTitle}>Descrição</h3>
                  <p>{selected.tribo.descricao}</p>
                </section>
              )}

              <section style={styles.section}>
                <h3 style={styles.sectionTitle}>Regras ({selected.regras.length})</h3>
                <ol style={styles.regrasList}>
                  {selected.regras.map((r) => (
                    <li key={r.ordem}>{r.texto}</li>
                  ))}
                </ol>
              </section>

              <section style={styles.section}>
                <h3 style={styles.sectionTitle}>
                  Threads-semente ({selected.sementes.length})
                </h3>
                {selected.sementes.map((s) => (
                  <article key={s.id_post} style={styles.sementeCard}>
                    <h4 style={styles.sementeTitle}>{s.titulo}</h4>
                    <p style={styles.sementeConteudo}>{s.conteudo}</p>
                  </article>
                ))}
              </section>
            </div>
            <footer style={styles.modalFooter}>
              <button
                onClick={() => aprovar(selected.tribo.id_tribo)}
                disabled={actionLoading}
                style={{ ...styles.actionButton, backgroundColor: '#28a745', flex: 1 }}
              >
                Aprovar
              </button>
              <button
                onClick={() => pedirAjuste(selected.tribo.id_tribo)}
                disabled={actionLoading}
                style={{ ...styles.actionButton, backgroundColor: '#ffc107', color: '#000', flex: 1 }}
              >
                Pedir ajuste
              </button>
              <button
                onClick={() => rejeitar(selected.tribo.id_tribo)}
                disabled={actionLoading}
                style={{ ...styles.actionButton, backgroundColor: '#dc3545', flex: 1 }}
              >
                Rejeitar
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: any } = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: { fontSize: '24px', color: '#333', margin: 0 },
  refreshButton: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: '#007bff',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer',
  },
  message: { fontSize: '16px', textAlign: 'center', color: '#555', padding: '40px 0' },
  list: { display: 'flex', flexDirection: 'column', gap: '12px' },
  card: {
    display: 'flex',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    alignItems: 'center',
  },
  cardIconWrap: { width: 64, height: 64, flexShrink: 0 },
  cardIcon: { width: 64, height: 64, borderRadius: 8, objectFit: 'cover' },
  cardIconPlaceholder: {
    width: 64,
    height: 64,
    borderRadius: 8,
    backgroundColor: '#e63946',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 26,
    fontWeight: 'bold',
  },
  cardBody: { flex: 1, minWidth: 0 },
  cardHeader: { display: 'flex', gap: 6, marginBottom: 6 },
  estadoBadge: (estado: string) => ({
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: estado === 'rascunho' ? '#ffc107' : '#17a2b8',
  }),
  tipoBadge: {
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 10,
    backgroundColor: '#e9ecef',
    color: '#495057',
  },
  cardTitle: { fontSize: 17, margin: '4px 0', color: '#212529' },
  cardDesc: { fontSize: 13, color: '#6c757d', margin: '4px 0' },
  cardMeta: { fontSize: 12, color: '#999', margin: '4px 0 0' },
  cardActions: { display: 'flex', gap: 6, flexDirection: 'column' as const },
  actionButton: {
    padding: '8px 14px',
    border: 'none',
    borderRadius: 4,
    color: '#fff',
    fontSize: 13,
    cursor: 'pointer',
    minWidth: 90,
  },
  modalOverlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 8,
    width: '100%',
    maxWidth: 700,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #e0e0e0',
  },
  modalTitle: { margin: 0, fontSize: 20, color: '#212529' },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: 28,
    cursor: 'pointer',
    color: '#999',
    lineHeight: 1,
  },
  modalBody: { padding: '16px 20px', overflowY: 'auto' as const, flex: 1 },
  modalMeta: { fontSize: 13, color: '#495057', lineHeight: 1.7 },
  section: { marginTop: 16 },
  sectionTitle: { fontSize: 14, color: '#495057', textTransform: 'uppercase' as const, marginBottom: 8 },
  regrasList: { paddingLeft: 24, color: '#212529', fontSize: 14, lineHeight: 1.6 },
  sementeCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
    borderLeft: '3px solid #e63946',
  },
  sementeTitle: { fontSize: 14, margin: '0 0 6px', color: '#212529' },
  sementeConteudo: { fontSize: 13, color: '#495057', margin: 0, whiteSpace: 'pre-wrap' as const },
  modalFooter: {
    display: 'flex',
    gap: 8,
    padding: '16px 20px',
    borderTop: '1px solid #e0e0e0',
  },
};

export default TribosPendentes;
